import React, { useState } from 'react';
import { Calendar, Clock, CheckCircle, XCircle, ChevronLeft, ChevronRight, MoreVertical } from 'lucide-react';
import { mockInspections } from '../../data/mockData';
export const InspectionManagement = () => {
  const [view, setView] = useState<'calendar' | 'list'>('calendar');
  return <div className="space-y-6 animate-in fade-in" data-id="element-667">
      <div data-id="element-668">
        <h1 className="text-2xl font-serif font-bold text-slate-900 dark:text-white mb-1" data-id="element-669">
          Inspection Management
        </h1>
        <p className="text-slate-500 dark:text-slate-400" data-id="element-670">
          Manage property viewing appointments across the platform.
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-671">
        {[{
        label: "Today's Inspections",
        value: '12',
        icon: Calendar,
        color: 'text-blue-600 dark:text-blue-400',
        bg: 'bg-blue-100 dark:bg-blue-400/10'
      }, {
        label: 'This Week',
        value: '45',
        icon: Clock,
        color: 'text-secondary',
        bg: 'bg-secondary/10'
      }, {
        label: 'Pending Confirmation',
        value: '8',
        icon: CheckCircle,
        color: 'text-emerald-600 dark:text-emerald-400',
        bg: 'bg-emerald-100 dark:bg-emerald-400/10'
      }, {
        label: 'Cancellation Rate',
        value: '4.2%',
        icon: XCircle,
        color: 'text-red-600 dark:text-red-400',
        bg: 'bg-red-100 dark:bg-red-400/10'
      }].map((stat, i) => {
        const Icon = stat.icon;
        return <div key={i} className="bg-white dark:bg-[#1e293b] p-5 rounded-2xl border border-slate-200 dark:border-slate-700 flex items-center gap-4" data-id="element-672">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.bg} ${stat.color}`} data-id="element-673">
                <Icon size={24} data-id="element-674" />
              </div>
              <div data-id="element-675">
                <div className="text-2xl font-bold text-slate-900 dark:text-white" data-id="element-676">
                  {stat.value}
                </div>
                <div className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider" data-id="element-677">
                  {stat.label}
                </div>
              </div>
            </div>;
      })}
      </div>

      {/* Main Content */}
      <div className="bg-white dark:bg-[#1e293b] rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col" data-id="element-678">
        {/* Header/Toolbar */}
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50" data-id="element-679">
          <div className="flex bg-slate-200 dark:bg-slate-900 rounded-lg p-1" data-id="element-680">
            <button onClick={() => setView('calendar')} className={`px-4 py-1.5 rounded-md text-sm font-bold transition-colors ${view === 'calendar' ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm dark:shadow-none' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`} data-id="element-681">
              Calendar View
            </button>
            <button onClick={() => setView('list')} className={`px-4 py-1.5 rounded-md text-sm font-bold transition-colors ${view === 'list' ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm dark:shadow-none' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`} data-id="element-682">
              List View
            </button>
          </div>

          {view === 'calendar' && <div className="flex items-center gap-4" data-id="element-683">
              <span className="text-slate-900 dark:text-white font-bold" data-id="element-684">
                Oct 23 - Oct 29, 2023
              </span>
              <div className="flex gap-1" data-id="element-685">
                <button className="p-1.5 bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded border border-slate-200 dark:border-transparent hover:bg-slate-50 dark:hover:bg-slate-600" data-id="element-686">
                  <ChevronLeft size={18} data-id="element-687" />
                </button>
                <button className="p-1.5 bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded border border-slate-200 dark:border-transparent hover:bg-slate-50 dark:hover:bg-slate-600" data-id="element-688">
                  <ChevronRight size={18} data-id="element-689" />
                </button>
              </div>
            </div>}
        </div>

        {/* Content Area */}
        <div className="flex-grow overflow-auto p-4" data-id="element-690">
          {view === 'calendar' ? <div className="min-w-[800px]" data-id="element-691">
              {/* Calendar Grid Header */}
              <div className="grid grid-cols-8 gap-2 mb-2" data-id="element-692">
                <div className="text-slate-500 text-xs font-bold text-right pr-2 pt-2" data-id="element-693">
                  Time
                </div>
                {['Mon 23', 'Tue 24', 'Wed 25', 'Thu 26', 'Fri 27', 'Sat 28', 'Sun 29'].map((day, i) => <div key={i} className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-2 text-center border border-slate-200 dark:border-slate-700" data-id="element-694">
                    <div className="text-slate-700 dark:text-slate-300 font-bold text-sm" data-id="element-695">
                      {day.split(' ')[0]}
                    </div>
                    <div className="text-slate-500 text-xs" data-id="element-696">
                      {day.split(' ')[1]}
                    </div>
                  </div>)}
              </div>

              {/* Calendar Grid Body */}
              <div className="space-y-2" data-id="element-697">
                {['09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM'].map((time, rowIdx) => <div key={rowIdx} className="grid grid-cols-8 gap-2" data-id="element-698">
                    <div className="text-slate-500 text-xs font-bold text-right pr-2 pt-2" data-id="element-699">
                      {time}
                    </div>
                    {[0, 1, 2, 3, 4, 5, 6].map(colIdx => {
                // Mock some bookings
                const hasBooking = rowIdx === 1 && colIdx === 2 || rowIdx === 3 && colIdx === 4 || rowIdx === 5 && colIdx === 1;
                const isConfirmed = colIdx % 2 === 0;
                return <div key={colIdx} className={`h-16 rounded-lg border ${hasBooking ? isConfirmed ? 'bg-emerald-100 border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/30' : 'bg-yellow-100 border-yellow-200 dark:bg-yellow-500/10 dark:border-yellow-500/30' : 'bg-slate-50 border-slate-100 dark:bg-slate-800/20 dark:border-slate-700/50'} p-2 relative group`} data-id="element-700">
                          {hasBooking && <>
                              <div className={`text-[10px] font-bold truncate ${isConfirmed ? 'text-emerald-700 dark:text-emerald-400' : 'text-yellow-700 dark:text-yellow-400'}`} data-id="element-701">
                                Luxury Duplex
                              </div>
                              <div className="text-[10px] text-slate-500 dark:text-slate-400 truncate" data-id="element-702">
                                Buyer: JD
                              </div>
                            </>}
                        </div>;
              })}
                  </div>)}
              </div>
            </div> : <table className="w-full text-left border-collapse min-w-[800px]" data-id="element-703">
              <thead data-id="element-704">
                <tr className="bg-slate-50 dark:bg-slate-900/50 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700" data-id="element-705">
                  <th className="p-4 pl-6" data-id="element-706">Property</th>
                  <th className="p-4" data-id="element-707">Buyer</th>
                  <th className="p-4" data-id="element-708">Seller</th>
                  <th className="p-4" data-id="element-709">Date & Time</th>
                  <th className="p-4" data-id="element-710">Status</th>
                  <th className="p-4 pr-6 text-right" data-id="element-711">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700/50" data-id="element-712">
                {mockInspections.map(inspection => <tr key={inspection.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors" data-id="element-713">
                    <td className="p-4 pl-6 font-bold text-slate-900 dark:text-slate-200" data-id="element-714">
                      {inspection.property.title}
                    </td>
                    <td className="p-4 text-sm text-slate-600 dark:text-slate-300" data-id="element-715">
                      John Doe
                    </td>
                    <td className="p-4 text-sm text-slate-600 dark:text-slate-300" data-id="element-716">
                      {inspection.seller}
                    </td>
                    <td className="p-4 text-sm text-slate-500 dark:text-slate-400" data-id="element-717">
                      {inspection.date} at {inspection.time}
                    </td>
                    <td className="p-4" data-id="element-718">
                      <span className={`px-2 py-1 text-[10px] font-bold uppercase rounded ${inspection.status === 'Confirmed' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400' : inspection.status === 'Pending' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/10 dark:text-yellow-400' : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'}`} data-id="element-719">
                        {inspection.status}
                      </span>
                    </td>
                    <td className="p-4 pr-6 text-right" data-id="element-720">
                      <button className="p-2 text-slate-400 hover:text-slate-900 dark:hover:text-white rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors" data-id="element-721">
                        <MoreVertical size={18} data-id="element-722" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>}
        </div>
      </div>
    </div>;
};